/**
 * 批量生成连贯帧序列弹窗（image_sequence 模式专用）
 *
 * 设计目标：
 * - 用户选择「锚定参考图」（已生成的某张帧 / 候选）
 * - 每帧独立编辑 prompt（支持 @ 引用角色 / 物品 / 场景）
 * - 后端按帧独立处理 img2img：anchor 是参考图1，@ 引用的资产从参考图2 起编号
 * - 完成后自动按顺序加入帧序列尾部
 *
 * 行为约束：
 * - 单次 1~8 帧
 * - 实时预估总积分（n × 单帧）
 * - 任意帧 anchor + 角色 + 物品 + 场景 总数 > 模型上限 → 禁用提交按钮 + 红色提示
 * - 模型不支持 img2img 时禁止 @ 引用资产（提交按钮 disable）
 *
 * 用 React Portal 渲染到 document.body，避免被 inspector 的 backdrop-blur 限制。
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Loader2,
  X,
  Coins,
  Sparkles,
  Cpu,
  Image as ImageIcon,
  AlertTriangle,
  Layers,
  Plus,
  Trash2,
  Wand2,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import toast from '../../../../components/Toast';
import { resolveBrowserMediaUrl } from '../../../../utils/storageUrl';
import {
  directorApi,
  DirectorApiError,
} from '../../services/directorApi';
import { api as imageApi, type ModelConfig } from '../../../../services/api';
import {
  MentionTextarea,
  extractMentions,
  type MentionCandidate,
  type MentionTextareaHandle,
} from './MentionTextarea';
import { PromptTemplateBar } from '../shared/PromptTemplateBar';
import {
  DIRECTOR_IMAGE_SIZE_OPTIONS,
  getDefaultDirectorImageSize,
  type DirectorAspectRatio,
  type DirectorImageSize,
} from '../../utils/imageSize';
import type {
  DirectorCharacter,
  DirectorProp,
  DirectorScene,
} from '../../services/directorApi';

const SECTION_LABEL =
  'flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500';
const FIELD =
  'w-full rounded-2xl border border-slate-200 bg-white px-3.5 py-2 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-amber-300 focus:ring-2 focus:ring-amber-100';

const MAX_FRAMES_PER_BATCH = 8;
const QUALITY_LABEL: Record<string, string> = {
  low: '标准',
  medium: '高清',
  high: '超清',
};

interface AnchorChoice {
  imageJobId: string;
  url: string;
  /** 标签：例如「序列 1」「候选 3」 */
  label: string;
}

interface Props {
  open: boolean;
  shotId: string;
  projectAspectRatio: DirectorAspectRatio;
  /** 当前用户余额 */
  currentCredits: number;
  /** 弹窗打开时已经收集好的可选锚定图列表（序列 + 候选） */
  anchors: AnchorChoice[];
  /** 默认 prompt（用户当前的分镜 prompt，用作首帧文案占位） */
  defaultPrompt?: string;
  /** 项目可引用的资产列表（用于 @ 唤起） */
  projectCharacters: DirectorCharacter[];
  projectProps: DirectorProp[];
  projectScenes: DirectorScene[];
  submitting?: boolean;
  onCancel: () => void;
  /**
   * 当 AI 多帧助手生成出 videoGuidance 文本时回调（除写帧 prompts 外的副作用）。
   * 父组件应当：调 directorApi.updateShot(shotId, { prompt }) → 触发 reload，
   * 让 ShotInspector 的「视频指导」textarea 反映新文本。
   */
  onVideoGuidanceGenerated?: (videoGuidance: string) => Promise<void> | void;
  /** 用户提交后的回调，由外层完成 toast / reload 工作 */
  onConfirm: (input: {
    /** 锚定参考图 ID；不选时为 null（纯走 @ 资产 → img2img，或 text2img） */
    anchorImageJobId: string | null;
    prompts: Array<{
      text: string;
      characterIds: string[];
      propIds: string[];
      sceneId: string | null;
    }>;
    modelId: string;
    size: string;
    quality: 'low' | 'medium' | 'high';
  }) => void;
}

/** localStorage 草稿 key 前缀（按分镜区分，避免不同分镜串数据） */
const DRAFT_KEY_PREFIX = 'director:batchFrameDraft:';

interface BatchFrameDraft {
  prompts: string[];
  anchorImageJobId: string | null;
  modelId: string;
  size: string;
  quality: 'low' | 'medium' | 'high';
  /** 写入时间（毫秒），过期 7 天后清理 */
  savedAt: number;
}

const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * 清除指定分镜的批量帧草稿。
 * 由父组件在批量生成成功后调用，避免下次打开还看到旧草稿。
 */
export function clearBatchFrameDraft(shotId: string): void {
  try {
    localStorage.removeItem(`${DRAFT_KEY_PREFIX}${shotId}`);
  } catch {
    // 忽略
  }
}

export function BatchFrameGenerateDialog({
  open,
  shotId,
  projectAspectRatio,
  currentCredits,
  anchors,
  defaultPrompt = '',
  projectCharacters,
  projectProps,
  projectScenes,
  submitting,
  onCancel,
  onVideoGuidanceGenerated,
  onConfirm,
}: Props) {
  const [anchorId, setAnchorId] = useState<string>('');
  const [prompts, setPrompts] = useState<string[]>([defaultPrompt]);
  // 每帧的 MentionTextarea 命令式 ref（给"模板快速插入"按钮用）
  const promptRefs = useRef<Array<MentionTextareaHandle | null>>([]);
  const setPromptRef = (idx: number) => (h: MentionTextareaHandle | null) => {
    promptRefs.current[idx] = h;
  };
  const [models, setModels] = useState<ModelConfig[]>([]);
  const [modelId, setModelId] = useState('');
  const [size, setSize] = useState<DirectorImageSize>(() =>
    getDefaultDirectorImageSize(projectAspectRatio)
  );
  const [quality, setQuality] = useState<'low' | 'medium' | 'high'>('low');

  const [previewLoading, setPreviewLoading] = useState(false);
  const [perFrameCredits, setPerFrameCredits] = useState<number | null>(null);
  const [totalCredits, setTotalCredits] = useState<number | null>(null);
  const [previewModelName, setPreviewModelName] = useState<string>('');
  const [willUseImg2Img, setWillUseImg2Img] = useState<boolean>(true);
  const [currentSeqLen, setCurrentSeqLen] = useState<number>(0);
  const [videoMax, setVideoMax] = useState<number | null>(null);
  const [remainingSlots, setRemainingSlots] = useState<number>(MAX_FRAMES_PER_BATCH);
  /** 图片模型 img2img 模式的参考图上限（每帧最多 anchor + 资产数 ≤ 此值） */
  const [imageMaxRef, setImageMaxRef] = useState<number | null>(null);

  // ---------------- AI 多帧助手（折叠面板） ----------------
  const [aiOpen, setAiOpen] = useState(false);
  const [aiScript, setAiScript] = useState('');
  const [aiFrameCount, setAiFrameCount] = useState<number>(4);
  const [aiLoading, setAiLoading] = useState(false);
  /** AI 多帧助手单次扣费（积分），从 publicSettings 拉。null = 还没拿到 / 失败时 fallback 不显示 */
  const [aiCost, setAiCost] = useState<number | null>(null);
  const [aiEnabled, setAiEnabled] = useState<boolean>(true);

  useEffect(() => {
    if (!open) return;
    directorApi
      .getPublicSettings()
      .then((res) => {
        setAiCost(res.frameStoryboardCreditsPerCall);
        setAiEnabled(res.frameStoryboardLlmEnabled);
      })
      .catch(() => {
        // 静默：失败不阻塞按钮
      });
  }, [open]);
  const mentionCandidates: MentionCandidate[] = useMemo(
    () => [
      ...projectCharacters.map((c) => ({
        id: c.id,
        name: c.name,
        kind: 'character' as const,
      })),
      ...projectProps.map((p) => ({
        id: p.id,
        name: p.name,
        kind: 'prop' as const,
      })),
      ...projectScenes.map((s) => ({
        id: s.id,
        name: s.name,
        kind: 'scene' as const,
      })),
    ],
    [projectCharacters, projectProps, projectScenes]
  );

  /**
   * 各帧的解析结果（按 prompts 数组顺序）：
   *   { text, characterIds, propIds, sceneId, sceneIdsExtra, refCount }
   * refCount 包含 anchor 占位（如果有），与后端 frameRefCount 计算一致
   */
  const parsedPrompts = useMemo(() => {
    const anchorCount = anchorId ? 1 : 0;
    return prompts.map((raw) => {
      const text = raw.trim();
      const parsed = extractMentions(
        text,
        projectCharacters.map((c) => ({ id: c.id, name: c.name })),
        projectProps.map((p) => ({ id: p.id, name: p.name })),
        projectScenes.map((s) => ({ id: s.id, name: s.name }))
      );
      const scene = parsed.sceneId
        ? projectScenes.find((s) => s.id === parsed.sceneId) ?? null
        : null;
      const sceneCountsAsRef = !!(scene && scene.settingImageJobId);
      const refCount =
        anchorCount +
        parsed.characterIds.length +
        parsed.propIds.length +
        (sceneCountsAsRef ? 1 : 0);
      return {
        text,
        characterIds: parsed.characterIds,
        propIds: parsed.propIds,
        sceneId: parsed.sceneId,
        sceneIdsExtra: parsed.sceneIdsExtra,
        scene,
        sceneCountsAsRef,
        refCount,
      };
    });
  }, [prompts, anchorId, projectCharacters, projectProps, projectScenes]);

  // 重置 + 预填默认锚定 + 尝试恢复 localStorage 草稿
  useEffect(() => {
    if (!open) return;

    const draftKey = `${DRAFT_KEY_PREFIX}${shotId}`;
    let restored: BatchFrameDraft | null = null;
    try {
      const raw = localStorage.getItem(draftKey);
      if (raw) {
        const parsed = JSON.parse(raw) as BatchFrameDraft;
        if (
          parsed &&
          typeof parsed.savedAt === 'number' &&
          Date.now() - parsed.savedAt < DRAFT_TTL_MS &&
          Array.isArray(parsed.prompts) &&
          parsed.prompts.length > 0
        ) {
          restored = parsed;
        } else {
          // 过期的草稿主动清理
          localStorage.removeItem(draftKey);
        }
      }
    } catch {
      // 忽略解析错误（可能是用户手动改了 localStorage）
    }

    if (restored) {
      setPrompts(restored.prompts);
      // anchor 仅在仍存在于 anchors 列表中时才恢复（用户可能删了那张图）
      const draftAnchorStillValid =
        !!restored.anchorImageJobId &&
        anchors.some((a) => a.imageJobId === restored.anchorImageJobId);
      setAnchorId(draftAnchorStillValid ? restored.anchorImageJobId! : '');
      setSize(restored.size as DirectorImageSize);
      setQuality(restored.quality);
      // modelId 在 imageApi.getModels 加载完成后再考虑覆盖
    } else {
      setPrompts([defaultPrompt || '']);
      // anchor 默认不预选——非必选了
      setAnchorId('');
      setSize(getDefaultDirectorImageSize(projectAspectRatio));
    }

    setPerFrameCredits(null);
    setTotalCredits(null);
    setPreviewModelName('');
    imageApi
      .getModels()
      .then((d) => {
        setModels(d.data);
        if (restored?.modelId) {
          // 草稿中的 modelId 仍存在则恢复，否则让 modelId 保持空
          if (d.data.some((m) => m.id === restored!.modelId)) {
            setModelId(restored!.modelId);
            return;
          }
        }
        if (!modelId && d.data.length > 0) {
          setModelId(d.data[0].id);
        }
      })
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, shotId]);

  const validParsedPrompts = useMemo(
    () => parsedPrompts.filter((p) => p.text.length > 0),
    [parsedPrompts]
  );

  // 实时预估积分（仅依赖帧数与模型/尺寸/画质）
  // 注意：count=0 时也要发一次 preview，以拿到 videoMax / remainingSlots / imageMaxRef 等"模型层"
  // 信息（这些不依赖 count），否则弹窗打开后 0 帧时显示的会是前端兜底默认（8 / null），
  // 与项目所选视频模型的实际上限不一致。
  useEffect(() => {
    if (!open) return;
    const count = validParsedPrompts.length;
    let cancelled = false;
    // 只有 count > 0 才显示 loading（避免空帧时让用户误以为在算积分）
    if (count > 0) setPreviewLoading(true);
    directorApi
      .previewBatchGenerateFrames(shotId, {
        // 后端要求 count >= 1，count=0 时传 1（仅用来拿模型层上限，UI 会忽略积分字段）
        count: Math.max(1, count),
        modelId: modelId || undefined,
        size,
        quality,
      })
      .then((preview) => {
        if (cancelled) return;
        if (count > 0) {
          // 真实有 prompt 时才采用积分；否则积分字段保持 null
          setPerFrameCredits(preview.perFrameCredits);
          setTotalCredits(preview.totalCredits);
        } else {
          setPerFrameCredits(null);
          setTotalCredits(null);
        }
        setPreviewModelName(preview.modelName);
        setWillUseImg2Img(preview.willUseImg2Img);
        setCurrentSeqLen(preview.currentSequenceLength);
        setVideoMax(preview.videoMaxReferenceImageCount);
        setRemainingSlots(preview.remainingSlots);
        setImageMaxRef(preview.imageMaxReferenceImageCount ?? null);
      })
      .catch((err) => {
        if (cancelled) return;
        // count=0 时静默失败（可能是模型未选导致 preview 抛错），不打扰用户
        if (count > 0) {
          toast.error(err instanceof DirectorApiError ? err.message : '预估积分失败');
        }
        setPerFrameCredits(null);
        setTotalCredits(null);
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, modelId, size, quality, validParsedPrompts.length]);

  // AI 帧数受 effectiveMax（视频模型剩余槽位）约束：当上限收紧时把当前 select 值往下夹
  useEffect(() => {
    if (!open) return;
    const cap = Math.max(1, Math.min(MAX_FRAMES_PER_BATCH, remainingSlots));
    setAiFrameCount((prev) => (prev > cap ? cap : prev));
  }, [open, remainingSlots]);

  // 自动保存草稿到 localStorage（debounce 500ms 避免每按键写一次）
  useEffect(() => {
    if (!open) return;
    const draftKey = `${DRAFT_KEY_PREFIX}${shotId}`;
    const t = setTimeout(() => {
      try {
        const draft: BatchFrameDraft = {
          prompts,
          anchorImageJobId: anchorId || null,
          modelId,
          size,
          quality,
          savedAt: Date.now(),
        };
        localStorage.setItem(draftKey, JSON.stringify(draft));
      } catch {
        // 忽略 quota / 隐私模式等错误
      }
    }, 500);
    return () => clearTimeout(t);
  }, [open, shotId, prompts, anchorId, modelId, size, quality]);

  if (!open) return null;
  if (typeof document === 'undefined') return null;

  const insufficient = totalCredits !== null && currentCredits < totalCredits;

  // 综合两个上限：BATCH_FRAME_LIMIT 与 视频模型 maxReferenceImageCount
  const effectiveMax = Math.min(MAX_FRAMES_PER_BATCH, Math.max(0, remainingSlots));
  const noSlot = effectiveMax === 0;
  const overSlot = validParsedPrompts.length > effectiveMax;

  // 各帧的图片模型参考图上限校验
  const overRefFrameIndices = imageMaxRef
    ? parsedPrompts
        .map((p, i) => ({ p, i }))
        .filter(({ p }) => p.text.length > 0 && p.refCount > imageMaxRef)
        .map(({ i }) => i)
    : [];
  const hasOverRef = overRefFrameIndices.length > 0;

  // 任一帧 @ 了资产但模型不支持 img2img
  const anyHasInjections = validParsedPrompts.some(
    (p) =>
      p.characterIds.length > 0 || p.propIds.length > 0 || p.sceneCountsAsRef
  );
  // anchor 选了但模型不支持 img2img → anchor 会被忽略，需要警告（但不阻止提交）
  const anchorIgnored = !!anchorId && !willUseImg2Img;
  const noImg2ImgWithInjections = anyHasInjections && !willUseImg2Img;

  const canSubmit =
    !!modelId &&
    validParsedPrompts.length > 0 &&
    !overSlot &&
    !noSlot &&
    !hasOverRef &&
    !noImg2ImgWithInjections &&
    totalCredits !== null &&
    !insufficient &&
    !submitting;

  const handleAddRow = () => {
    if (prompts.length >= effectiveMax) return;
    setPrompts([...prompts, '']);
  };
  const handleRemoveRow = (idx: number) => {
    if (prompts.length <= 1) return;
    setPrompts(prompts.filter((_, i) => i !== idx));
  };
  const handleChangeRow = (idx: number, value: string) => {
    setPrompts(prompts.map((p, i) => (i === idx ? value.slice(0, 500) : p)));
  };

  /**
   * AI 多帧助手：把一句话剧本拆成 N 帧 prompt + 分镜级视频指导，灌进现有的输入框。
   *
   * 行为：
   * - 已有非空 prompt 时弹 confirm 二次确认（避免误覆盖；提示同时会替换分镜的视频指导）
   * - 调后端拿到 frames + videoGuidance：
   *   - frames 重建 prompts 数组（拼上 @角色名/@物品名/@场景名 让 extractMentions 能识别）
   *   - videoGuidance 通过 onVideoGuidanceGenerated 回调让父组件写回 shot.prompt
   * - 失败 toast 通用文案
   */
  const handleAiStoryboard = async () => {
    const script = aiScript.trim();
    if (!script) {
      toast.error('请填写一句话剧本');
      return;
    }
    if (aiFrameCount < 1 || aiFrameCount > MAX_FRAMES_PER_BATCH) {
      toast.error(`帧数必须在 1~${MAX_FRAMES_PER_BATCH} 之间`);
      return;
    }
    if (aiFrameCount > effectiveMax) {
      toast.error(`视频模型剩余可加帧数为 ${effectiveMax}，请减少 AI 帧数`);
      return;
    }
    const hasNonEmpty = prompts.some((p) => p.trim().length > 0);
    const costNote =
      typeof aiCost === 'number' && aiCost > 0
        ? `本次将扣 ${aiCost} 积分（按调用计，无论生成几帧）。失败自动退还。`
        : '';
    if (hasNonEmpty) {
      const ok = window.confirm(
        `AI 生成会替换当前已填写的 ${prompts.filter((p) => p.trim()).length} 帧提示词，并填写分镜的「视频指导」。${costNote}是否继续？`
      );
      if (!ok) return;
    } else if (costNote) {
      // 没有覆盖风险时，仅在有扣费时弹一次扣费确认
      const ok = window.confirm(`${costNote}是否继续？`);
      if (!ok) return;
    }
    setAiLoading(true);
    try {
      const result = await directorApi.aiStoryboardForShot(shotId, {
        scriptText: script,
        frameCount: aiFrameCount,
        anchorImageJobId: anchorId || null,
      });
      // 把 frames 拼成带 @ 的纯文本，让 MentionTextarea / extractMentions 自动识别
      const charNameMap = new Map(projectCharacters.map((c) => [c.id, c.name]));
      const propNameMap = new Map(projectProps.map((p) => [p.id, p.name]));
      const sceneNameMap = new Map(projectScenes.map((s) => [s.id, s.name]));
      const newPrompts = result.frames.map((f) => {
        const mentions: string[] = [];
        for (const cid of f.characterIds) {
          const name = charNameMap.get(cid);
          if (name) mentions.push(`@${name}`);
        }
        for (const pid of f.propIds) {
          const name = propNameMap.get(pid);
          if (name) mentions.push(`@${name}`);
        }
        if (f.sceneId) {
          const name = sceneNameMap.get(f.sceneId);
          if (name) mentions.push(`@${name}`);
        }
        const mentionPart = mentions.length > 0 ? ` ${mentions.join(' ')}` : '';
        return (f.text + mentionPart).slice(0, 500);
      });
      setPrompts(newPrompts);

      // 把 videoGuidance 通过回调写回 shot.prompt（父组件做 updateShot + reload）
      if (result.videoGuidance && onVideoGuidanceGenerated) {
        try {
          await onVideoGuidanceGenerated(result.videoGuidance);
        } catch (e) {
          console.error('[batchFrame] onVideoGuidanceGenerated 失败', e);
          // 视频指导写回失败不阻塞 frames 写入，给个提示
          toast.error('视频指导写回失败，请稍后手工编辑');
        }
      }

      const generated = result.frames.length;
      const requested = aiFrameCount;
      const guidanceMsg = result.videoGuidance ? '+ 视频指导 ' : '';
      if (generated < requested) {
        toast.success(
          `AI 仅生成了 ${generated} 帧 ${guidanceMsg}（已请求 ${requested}），请检查后批量生成`
        );
      } else {
        toast.success(`已生成 ${generated} 帧 prompt ${guidanceMsg}请检查后批量生成`);
      }
      setAiOpen(false);
    } catch (err) {
      const msg = err instanceof DirectorApiError ? err.message : 'AI 生成失败，请稍后重试';
      toast.error(msg);
    } finally {
      setAiLoading(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
      <button
        type="button"
        aria-label="关闭"
        className="absolute inset-0 cursor-default"
        onClick={onCancel}
      />
      <div
        className="relative flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-[28px] bg-white shadow-2xl shadow-slate-950/30"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 顶部 hero */}
        <div className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 p-5 text-white">
          <div className="absolute right-[-40px] top-[-40px] h-32 w-32 rounded-full bg-amber-400/20 blur-2xl" />
          <div className="relative flex items-start justify-between gap-3">
            <div>
              <div className="mb-1.5 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-0.5 text-[11px] font-medium text-slate-200">
                <Sparkles className="h-3 w-3 text-amber-200" />
                批量生成连贯帧序列
              </div>
              <h2 className="text-lg font-semibold leading-tight">
                批量生成多帧（可选锚定参考图）
              </h2>
              <p className="mt-1 max-w-md text-xs leading-5 text-slate-300">
                可选 1 张锚定参考图保证主体一致；各帧也可独立 @ 引用角色 / 物品 / 场景作为额外参考图。
                单次最多 {MAX_FRAMES_PER_BATCH} 帧，且不超过视频模型参考图上限。
              </p>
            </div>
            <button
              type="button"
              onClick={onCancel}
              className="rounded-full bg-white/10 p-1.5 text-white transition hover:bg-white/20"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-hidden bg-slate-50">
          <div className="grid h-full grid-cols-1 lg:grid-cols-[1fr_340px]">
            {/* ============ 左栏：编辑区（独立滚动） ============ */}
            <div className="space-y-4 overflow-y-auto p-5 lg:border-r lg:border-slate-200">
          {/* AI 多帧助手（折叠面板） */}
          <div className="rounded-2xl border border-purple-200 bg-gradient-to-br from-purple-50 via-white to-fuchsia-50 ring-1 ring-purple-100">
            <button
              type="button"
              onClick={() => setAiOpen((v) => !v)}
              className="flex w-full items-center justify-between gap-2 px-4 py-2.5 text-left"
            >
              <span className="flex items-center gap-2 text-sm font-semibold text-purple-900">
                <Wand2 className="h-4 w-4 text-purple-600" />
                AI 帮我生成多帧提示词
                <span className="rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-medium text-purple-700">
                  Beta
                </span>
                {typeof aiCost === 'number' && aiCost > 0 && (
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-700">
                    {aiCost} 积分/次
                  </span>
                )}
              </span>
              {aiOpen ? (
                <ChevronUp className="h-4 w-4 text-purple-500" />
              ) : (
                <ChevronDown className="h-4 w-4 text-purple-500" />
              )}
            </button>
            {aiOpen && (
              <div className="border-t border-purple-100 px-4 py-3 space-y-3">
                <div>
                  <label className="mb-1 block text-[11px] font-medium text-purple-800">
                    一句话剧本（≤500 字）
                  </label>
                  <textarea
                    value={aiScript}
                    onChange={(e) => setAiScript(e.target.value.slice(0, 500))}
                    rows={2}
                    placeholder="例如：少女在樱花树下捡起花瓣，慢慢抬头微笑"
                    className="w-full resize-none rounded-xl border border-purple-200 bg-white px-3 py-2 text-sm outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-100"
                    disabled={aiLoading}
                  />
                </div>
                <div className="flex items-end gap-3">
                  <div className="flex-1">
                    <label className="mb-1 block text-[11px] font-medium text-purple-800">
                      生成帧数
                      {effectiveMax < MAX_FRAMES_PER_BATCH && (
                        <span className="ml-1.5 text-[10px] font-normal normal-case tracking-normal text-purple-600/80">
                          · 视频模型剩余 {effectiveMax} 帧
                        </span>
                      )}
                    </label>
                    <select
                      value={Math.min(aiFrameCount, Math.max(1, effectiveMax))}
                      onChange={(e) => setAiFrameCount(Number(e.target.value))}
                      disabled={aiLoading || noSlot}
                      className="w-full rounded-xl border border-purple-200 bg-white px-3 py-2 text-sm outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-100 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {Array.from(
                        { length: Math.max(1, Math.min(MAX_FRAMES_PER_BATCH, effectiveMax)) },
                        (_, i) => i + 1
                      ).map((n) => (
                        <option key={n} value={n}>
                          {n} 帧
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    type="button"
                    onClick={handleAiStoryboard}
                    disabled={aiLoading || !aiScript.trim() || noSlot || !aiEnabled}
                    title={!aiEnabled ? '管理员未启用 AI 多帧助手' : undefined}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-purple-600 to-fuchsia-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {aiLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Wand2 className="h-4 w-4" />
                    )}
                    AI 生成 {Math.min(aiFrameCount, Math.max(1, effectiveMax))} 帧
                    {typeof aiCost === 'number' && aiCost > 0 && (
                      <span className="ml-1 rounded-full bg-white/20 px-1.5 py-0.5 text-[10px] font-semibold">
                        {aiCost} 积分
                      </span>
                    )}
                  </button>
                </div>
                <p className="text-[11px] leading-4 text-purple-700/80">
                  AI 会同时生成 N 帧画面 prompt 和分镜的「视频指导」（动作 / 节奏 / 过渡），
                  并按项目内的角色 / 物品 / 场景智能引用。可手动二次编辑。
                  {typeof aiCost === 'number' && aiCost > 0 && (
                    <>
                      <br />
                      <b>每次调用扣 {aiCost} 积分</b>（按调用计，无论生成几帧）。失败自动退还。
                    </>
                  )}
                </p>
              </div>
            )}
          </div>

          {/* 视频模型参考图上限提示（注意：与图片模型 imageMaxRef 是两个独立约束） */}
          {videoMax !== null && (
            <div
              className={`flex items-start gap-2 rounded-2xl px-3 py-2 text-[11px] leading-4 ${
                noSlot
                  ? 'bg-rose-50 text-rose-700 ring-1 ring-rose-100'
                  : remainingSlots <= 2
                    ? 'bg-amber-50 text-amber-700 ring-1 ring-amber-100'
                    : 'bg-slate-100 text-slate-600 ring-1 ring-slate-200'
              }`}
            >
              <AlertTriangle
                className={`mt-0.5 h-3 w-3 shrink-0 ${
                  noSlot
                    ? 'text-rose-500'
                    : remainingSlots <= 2
                      ? 'text-amber-500'
                      : 'text-slate-400'
                }`}
              />
              <span className="flex-1">
                视频模型最多支持 <b>{videoMax}</b> 张参考图，序列已有 <b>{currentSeqLen}</b> 张，
                {noSlot ? (
                  <>已达上限，请先移除一些帧再批量生成。</>
                ) : (
                  <>本次最多再加 <b>{remainingSlots}</b> 张。</>
                )}
              </span>
            </div>
          )}

          {/* 帧 prompts 列表 */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className={SECTION_LABEL}>
                <Layers className="h-3 w-3" /> 帧 prompts（{validParsedPrompts.length}/{effectiveMax || MAX_FRAMES_PER_BATCH}）
              </label>
              <button
                type="button"
                onClick={handleAddRow}
                disabled={prompts.length >= effectiveMax || noSlot}
                className="inline-flex items-center gap-1 rounded-xl bg-amber-50 px-2 py-1 text-[11px] font-semibold text-amber-700 transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Plus className="h-3 w-3" /> 添加一帧
              </button>
            </div>
            <div className="space-y-1.5">
              {prompts.map((p, idx) => {
                const parsed = parsedPrompts[idx];
                const overRef =
                  imageMaxRef !== null &&
                  parsed.text.length > 0 &&
                  parsed.refCount > imageMaxRef;
                return (
                  <div
                    key={idx}
                    className={`flex items-start gap-2 rounded-2xl bg-white p-2 ring-1 ${
                      overRef ? 'ring-rose-300' : 'ring-slate-100'
                    }`}
                  >
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-[11px] font-bold text-amber-700">
                      {idx + 1}
                    </span>
                    <div className="flex-1 space-y-1.5">
                      <MentionTextarea
                        ref={setPromptRef(idx)}
                        value={p}
                        onChange={(v) => handleChangeRow(idx, v)}
                        candidates={mentionCandidates}
                        rows={2}
                        maxLength={500}
                        placeholder={
                          idx === 0
                            ? '描述第 1 帧的画面（输入 @ 引用角色 / 物品 / 场景）'
                            : `描述第 ${idx + 1} 帧的画面（输入 @ 引用角色 / 物品 / 场景）`
                        }
                        className="w-full resize-none rounded-xl border border-transparent bg-transparent px-2 py-1 text-sm text-slate-900 outline-none focus:border-amber-200 focus:bg-amber-50/30"
                      />
                      {/* 模板快速插入（每行独立） */}
                      <PromptTemplateBar
                        onInsert={(text) => promptRefs.current[idx]?.insertText(text)}
                      />
                      {/* 注入徽标（紧凑） */}
                      {parsed.text.length > 0 &&
                        (parsed.characterIds.length > 0 ||
                          parsed.propIds.length > 0 ||
                          parsed.scene) && (
                          <div className="flex flex-wrap gap-1">
                            {parsed.characterIds.map((cid) => {
                              const c = projectCharacters.find((x) => x.id === cid);
                              if (!c) return null;
                              return (
                                <span
                                  key={`f${idx}-char-${cid}`}
                                  className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-1.5 py-0.5 text-[10px] font-medium text-sky-700 ring-1 ring-sky-200"
                                >
                                  <span className="text-sky-500">@</span>
                                  {c.name}
                                </span>
                              );
                            })}
                            {parsed.propIds.map((pid) => {
                              const pp = projectProps.find((x) => x.id === pid);
                              if (!pp) return null;
                              return (
                                <span
                                  key={`f${idx}-prop-${pid}`}
                                  className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 ring-1 ring-amber-200"
                                >
                                  <span className="text-amber-500">@</span>
                                  {pp.name}
                                </span>
                              );
                            })}
                            {parsed.scene && (
                              <span
                                key={`f${idx}-scene`}
                                className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 ring-1 ring-emerald-200"
                              >
                                <span className="text-emerald-500">@</span>
                                {parsed.scene.name}
                              </span>
                            )}
                          </div>
                        )}
                      {overRef && imageMaxRef !== null && (
                        <div className="flex items-start gap-1.5 rounded-lg bg-rose-50 px-2 py-1 text-[10px] leading-4 text-rose-700">
                          <AlertTriangle className="mt-[1px] h-3 w-3 shrink-0" />
                          <span>
                            参考图数量 {parsed.refCount}（
                            {anchorId ? `锚定 1 + 注入 ${parsed.refCount - 1}` : `@ 注入 ${parsed.refCount}`}
                            ）超过模型上限 {imageMaxRef}
                          </span>
                        </div>
                      )}
                      {parsed.sceneIdsExtra.length > 0 && parsed.scene && (
                        <div className="flex items-start gap-1.5 rounded-lg bg-amber-50 px-2 py-1 text-[10px] leading-4 text-amber-700">
                          <AlertTriangle className="mt-[1px] h-3 w-3 shrink-0" />
                          <span>
                            每帧只能引用一个场景，已采用 @{parsed.scene.name}；其余{' '}
                            {parsed.sceneIdsExtra.length} 个被忽略
                          </span>
                        </div>
                      )}
                    </div>
                    {prompts.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveRow(idx)}
                        className="rounded-lg p-1.5 text-slate-400 transition hover:bg-rose-50 hover:text-rose-600"
                        title="移除该帧"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
            {/* ============ 左栏结束 ============ */}
            </div>

            {/* ============ 右栏：参数 / 信息 / 操作（lg 以上独立滚动） ============ */}
            <div className="space-y-4 overflow-y-auto bg-white p-5 lg:bg-slate-50">
              {/* 锚定参考图选择（可选） */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className={SECTION_LABEL}>
                    <ImageIcon className="h-3 w-3" /> 锚定参考图
                    <span className="ml-1 text-[10px] font-normal normal-case tracking-normal text-slate-400">
                      可选
                    </span>
                  </label>
                  {anchorId && (
                    <button
                      type="button"
                      onClick={() => setAnchorId('')}
                      className="text-[11px] font-medium text-slate-400 transition hover:text-rose-500"
                      title="不使用锚定图"
                    >
                      清除
                    </button>
                  )}
                </div>
                {anchors.length === 0 ? (
                  <div className="flex items-start gap-2 rounded-2xl border border-dashed border-slate-200 bg-white/60 px-3 py-2.5 text-[11px] leading-4 text-slate-500">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
                    <span>
                      当前分镜还没有可作锚定的图。可在 prompt 中 @ 引用资产作为参考。
                    </span>
                  </div>
                ) : (
                  <div className="grid max-h-[180px] grid-cols-3 gap-2 overflow-y-auto rounded-2xl bg-white p-2 ring-1 ring-slate-100">
                    {anchors.map((a) => (
                      <button
                        key={a.imageJobId}
                        type="button"
                        onClick={() =>
                          setAnchorId(anchorId === a.imageJobId ? '' : a.imageJobId)
                        }
                        className={`group relative aspect-square overflow-hidden rounded-xl ring-2 transition ${
                          anchorId === a.imageJobId
                            ? 'ring-amber-500'
                            : 'ring-transparent hover:ring-amber-200'
                        }`}
                        title={a.label}
                      >
                        <img
                          src={resolveBrowserMediaUrl(a.url)}
                          alt=""
                          className="h-full w-full object-cover"
                          loading="lazy"
                        />
                        <span className="absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-black/70 to-transparent px-1.5 py-0.5 text-[10px] text-white">
                          {a.label}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                {!anchorId && anchors.length > 0 && (
                  <p className="text-[11px] leading-4 text-slate-400">
                    未选锚定时主体一致性会下降，建议至少选 1 张或在 prompt 里 @ 引用一个角色 / 物品。
                  </p>
                )}
              </div>

          {/* 模型 + 尺寸 + 画质 */}
          <div className="grid grid-cols-1 gap-3">
            <div className="space-y-1.5">
              <label className={SECTION_LABEL}>
                <Cpu className="h-3 w-3" /> 图片模型
              </label>
              <select
                value={modelId}
                onChange={(e) => setModelId(e.target.value)}
                className={FIELD}
              >
                <option value="">默认（项目设置）</option>
                {models.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <label className={SECTION_LABEL}>尺寸</label>
              <div className="grid grid-cols-3 gap-1">
                {DIRECTOR_IMAGE_SIZE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setSize(opt.value as typeof size)}
                    className={`rounded-lg border px-1.5 py-1.5 text-[11px] font-medium leading-none transition ${
                      size === opt.value
                        ? 'border-slate-950 bg-slate-950 text-white shadow-sm'
                        : 'border-slate-200 bg-white text-slate-700 hover:border-slate-400'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <label className={SECTION_LABEL}>画质</label>
            <div className="grid grid-cols-3 gap-1.5">
              {[
                { value: 'low', label: '标准' },
                { value: 'medium', label: '高清' },
                { value: 'high', label: '超清' },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setQuality(opt.value as typeof quality)}
                  className={`rounded-2xl border px-2 py-2 text-sm font-medium transition ${
                    quality === opt.value
                      ? 'border-slate-950 bg-slate-950 text-white shadow-md'
                      : 'border-slate-200 bg-white text-slate-700 hover:border-slate-400'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* 模型预览 */}
          <div className="rounded-2xl bg-white p-3 ring-1 ring-slate-100">
            <div className="flex items-center justify-between gap-3 text-[11px]">
              <span className="uppercase tracking-[0.18em] text-slate-400">实际模型</span>
              <span className="truncate text-sm font-semibold text-slate-900">
                {previewModelName || '—'}
              </span>
            </div>
            {imageMaxRef !== null && (
              <div className="mt-1 flex items-center justify-between gap-3 text-[11px]">
                <span className="uppercase tracking-[0.18em] text-slate-400">
                  图片模型参考图上限
                </span>
                <span className="text-slate-700">{imageMaxRef} 张 / 帧</span>
              </div>
            )}
            {noImg2ImgWithInjections && (
              <div className="mt-2 flex items-start gap-1.5 rounded-xl bg-rose-50 px-2.5 py-1.5 text-[11px] leading-4 text-rose-700">
                <AlertTriangle className="mt-[1px] h-3 w-3 shrink-0" />
                <span>
                  当前模型未配置 img2img 渠道，无法使用 @ 引用资产生成。请改选支持图生图的模型，或去掉所有 @ 引用。
                </span>
              </div>
            )}
            {anchorIgnored && !noImg2ImgWithInjections && (
              <div className="mt-2 flex items-start gap-1.5 rounded-xl bg-amber-50 px-2.5 py-1.5 text-[11px] leading-4 text-amber-800">
                <AlertTriangle className="mt-[1px] h-3 w-3 shrink-0" />
                <span>
                  当前模型未配置 img2img 渠道，已选的锚定参考图将被忽略，将走 text2img 生成。
                </span>
              </div>
            )}
            {!willUseImg2Img && !anyHasInjections && !anchorId && (
              <div className="mt-2 flex items-start gap-1.5 rounded-xl bg-slate-50 px-2.5 py-1.5 text-[11px] leading-4 text-slate-600">
                <AlertTriangle className="mt-[1px] h-3 w-3 shrink-0 text-slate-400" />
                <span>
                  没有锚定也没有 @ 引用，将走纯 text2img 生成。各帧主体一致性会显著下降。
                </span>
              </div>
            )}
          </div>

          {/* 积分 */}
          <div className="rounded-2xl bg-white p-3 ring-1 ring-slate-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-slate-400">
                <Coins className="h-3 w-3" /> 预计消耗
              </div>
              <div className="text-base font-semibold text-amber-700">
                {previewLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                ) : totalCredits !== null && perFrameCredits !== null ? (
                  <span>
                    {totalCredits} 积分
                    <span className="ml-1 text-[11px] font-normal text-slate-500">
                      （{perFrameCredits} × {validParsedPrompts.length}）
                    </span>
                  </span>
                ) : (
                  '—'
                )}
              </div>
            </div>
            <div className="mt-2 flex items-center justify-between text-[11px]">
              <span className="text-slate-400">当前余额</span>
              <span className={insufficient ? 'text-rose-500' : 'text-slate-600'}>
                {currentCredits} 积分
              </span>
            </div>
          </div>

          {insufficient && (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-3 py-2.5 text-xs text-rose-700">
              积分不足，请先充值或减少帧数
            </div>
          )}

          <p className="text-[11px] leading-5 text-slate-500">
            生成成功后，新帧会按顺序加入帧序列尾部（pending 状态会先占位，完成后自动显示图）。任一帧失败不影响其它帧，可随时单独移除补生成。
          </p>
            {/* ============ 右栏结束 ============ */}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-100 bg-white px-5 py-3">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
          >
            取消
          </button>
          <button
            type="button"
            onClick={() =>
              onConfirm({
                anchorImageJobId: anchorId || null,
                prompts: validParsedPrompts.map((p) => ({
                  text: p.text,
                  characterIds: p.characterIds,
                  propIds: p.propIds,
                  sceneId: p.sceneId,
                })),
                modelId,
                size,
                quality,
              })
            }
            disabled={!canSubmit}
            className="inline-flex items-center gap-2 rounded-2xl bg-amber-600 px-4 py-2 text-sm font-semibold text-white shadow-md transition hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Layers className="h-4 w-4" />
            )}
            {totalCredits !== null
              ? `生成 ${validParsedPrompts.length} 帧（${totalCredits} 积分 / ${QUALITY_LABEL[quality]}）`
              : '生成'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
